import React, { useCallback, useContext } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';
import ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';

import { ClassicEditorContext } from '@confluence/editor-shared/entry-points/ClassicEditorContext.ts';
import { useEditorContentModeValue } from '@confluence/editor-content-mode-button/entry-points/useEditorContentModeValue.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

import { ADFRenderer } from './ADFRenderer.tsx';
import type { ADFRendererProps } from './ADFRendererComponent.tsx';

export interface ADFRendererWithExtensionsProps extends ADFRendererProps {
	spaceKey: string;
	spaceId: string | undefined;
}

/**
 * A fully featured ADF renderer component preconfigured with relevant Confluence providers and handlers for rendering extensions
 * Should be used whenever nested Confluence content needs to be displayed
 */
export const ADFRendererWithExtensions = ({
	contentId,
	document,
	spaceKey,
	spaceId,
}: ADFRendererWithExtensionsProps): React.JSX.Element => {
	const { environment, cloudId, userId, activationId } = useSessionData();
	const { getExtensionHandlers, populateExtensionProviders } = useContext(ClassicEditorContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isLivePage = useIsCurrentPageLive();
	const { mode } = useEditorContentModeValue({ contentId });

	const extensionHandlers = getExtensionHandlers?.({
		adf: document,
		contentId,
	});

	const getDataProviders = useCallback(() => {
		const providerFactory = ProviderFactory.create({});
		const intlStub = {
			formatMessage: (message: any) => message?.defaultMessage,
		};

		if (populateExtensionProviders) {
			void populateExtensionProviders(providerFactory, {
				activationId,
				accountId: userId,
				cloudId,
				containerId: spaceKey,
				contentId,
				contentType: 'page',
				createAnalyticsEvent,
				environment,
				extensionHandlers,
				intl: intlStub,
				spaceKey,
				spaceId,
				isLivePage,
			});
		}

		return providerFactory;
	}, [
		activationId,
		cloudId,
		environment,
		extensionHandlers,
		contentId,
		createAnalyticsEvent,
		populateExtensionProviders,
		spaceKey,
		spaceId,
		userId,
		isLivePage,
	]);

	return (
		<RendererActionsContext>
			<ADFRenderer
				allowHeadingAnchorLinks={false}
				appearance="max"
				contentId={contentId}
				dataProviders={getDataProviders()}
				document={document}
				extensionHandlers={extensionHandlers}
				contentMode={mode}
				isTopLevelRenderer={false}
			/>
		</RendererActionsContext>
	);
};
